/**
 * Validation Strategy Implementations
 * Handle four validation modes: free, blocked, indicators, auto-save
 *
 * Child forms are validated by the engine that renders them. Each mounted child registers its
 * FormEngine in ChildFormRegistryStore, so validation uses the child's current values and its
 * own schema rules. A strategy belongs to one container (its scope), so it only sees that
 * container's child forms.
 */

import type { IValidationStrategy } from '../types/index';
import { useChildFormRegistryStore, childFormKey } from '../stores/ChildFormRegistryStore';

/**
 * Validate one child form of a container using its registered engine.
 * Errors carry the child's own formID, not the registry key.
 *
 * A child whose schema is not known (still loading, failed to load, or never registered) cannot
 * be checked. That is reported as an error rather than treated as valid, so Save and tab
 * switching cannot skip a required field in a step that was never opened.
 */
export function validateChildForm(scope: string, formID: string): any[] {
  const outcome = useChildFormRegistryStore.getState().validateChild(childFormKey(scope, formID));
  if (!outcome) {
    return [
      {
        field: formID,
        formID,
        code: 'CHILD_FORM_UNAVAILABLE',
        rule: 'childForm',
        message: `Form ${formID} could not be checked yet. Open it and try again.`,
        value: undefined,
      },
    ];
  }
  return outcome.errors.map((error) => ({ ...error, formID }));
}

/**
 * Validate every child form in `forms` (keys are child form IDs) and return a flat list of
 * errors, each tagged with its formID.
 */
function validateChildForms(scope: string, forms: Record<string, unknown>): any[] {
  const errors: any[] = [];
  for (const formID of Object.keys(forms)) {
    errors.push(...validateChildForm(scope, formID));
  }
  return errors;
}

/**
 * Free Navigation Validation Strategy
 * Allow switching between forms without validation
 * Validate all forms on submit
 */
export class FreeNavigationValidationStrategy implements IValidationStrategy {
  constructor(private readonly scope: string = '') {}

  async canSwitchForm(): Promise<boolean> {
    return true; // Always allow
  }

  async validateBeforeSave(forms: Record<string, any>): Promise<any[]> {
    return validateChildForms(this.scope, forms);
  }

  onValidationError(errors: any[]): void {
    console.warn('Validation errors on save:', errors);
  }
}

/**
 * Blocked Navigation Validation Strategy
 * Block switching to next form if current is invalid
 */
export class BlockedNavigationValidationStrategy implements IValidationStrategy {
  constructor(private readonly scope: string = '') {}

  async canSwitchForm(formID?: string): Promise<boolean> {
    if (!formID) return true;
    return validateChildForm(this.scope, formID).length === 0;
  }

  async validateBeforeSave(forms: Record<string, any>): Promise<any[]> {
    return validateChildForms(this.scope, forms);
  }

  onValidationError(errors: any[]): void {
    console.error('Cannot proceed - form has errors:', errors);
  }
}

/**
 * Error Indicators Validation Strategy
 * Allow switching, but show error badges on invalid forms
 */
export class ErrorIndicatorsValidationStrategy implements IValidationStrategy {
  private formErrors = new Map<string, any[]>();

  constructor(private readonly scope: string = '') {}

  async canSwitchForm(): Promise<boolean> {
    return true; // Allow switching
  }

  async validateBeforeSave(forms: Record<string, any>): Promise<any[]> {
    for (const formID of Object.keys(forms)) {
      this.formErrors.set(formID, validateChildForm(this.scope, formID));
    }
    return Array.from(this.formErrors.values()).flat();
  }

  onValidationError(errors: any[]): void {
    // Update UI with error badges
    console.warn('Show error indicators for:', errors);
  }

  getFormErrors(formID: string): any[] {
    return this.formErrors.get(formID) || [];
  }
}

/**
 * Auto-Save Validation Strategy
 * Validate on change, auto-save valid forms
 */
export class AutoSaveValidationStrategy implements IValidationStrategy {
  private autoSaveTimer: ReturnType<typeof setTimeout> | null = null;
  private autoSaveDelay = 1000; // 1 second

  constructor(private readonly scope: string = '') {}

  async canSwitchForm(): Promise<boolean> {
    return true;
  }

  async validateBeforeSave(forms: Record<string, any>): Promise<any[]> {
    return validateChildForms(this.scope, forms);
  }

  onValidationError(errors: any[]): void {
    // Schedule auto-save after delay
    if (this.autoSaveTimer) {
      clearTimeout(this.autoSaveTimer);
    }
    this.autoSaveTimer = setTimeout(() => {
      this.performAutoSave();
    }, this.autoSaveDelay);
  }

  private performAutoSave(): void {
    console.log('Performing auto-save of valid data');
  }
}

/**
 * Validation Strategy Factory
 */
export class ValidationStrategyFactory {
  /**
   * @param scope id of the container control; child forms are looked up under this scope
   */
  static createStrategy(
    mode?: 'free' | 'blocked' | 'indicators' | 'auto-save' | null,
    scope: string = ''
  ): IValidationStrategy {
    // Default to 'free' if mode is undefined or null
    const resolvedMode = mode || 'free';

    switch (resolvedMode) {
      case 'free':
        return new FreeNavigationValidationStrategy(scope);
      case 'blocked':
        return new BlockedNavigationValidationStrategy(scope);
      case 'indicators':
        return new ErrorIndicatorsValidationStrategy(scope);
      case 'auto-save':
        return new AutoSaveValidationStrategy(scope);
      default:
        throw new Error(`Unknown validation mode: ${resolvedMode}`);
    }
  }
}
